import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Calendar } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import SEOHead from '@/components/seo/SEOHead';
import JsonLd from '@/components/seo/JsonLd';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import { blogPosts } from '@/data/blogPosts';

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://ifixappleservicecenter.in"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Blog",
      "item": "https://ifixappleservicecenter.in/blog"
    }
  ]
};

const Blog = () => {
  return (
    <Layout>
      <SEOHead
        title="Apple Repair Tips & Guides | iFix Apple Care Blog"
        description="Learn iPhone, MacBook, iPad & Apple Watch repair tips from Bihar's top service center. Troubleshooting guides, maintenance tips & expert advice. Read now!"
        url="https://ifixappleservicecenter.in/blog"
      />
      <JsonLd data={breadcrumbSchema} />
      {/* Hero Section */}
      <section className="section-padding bg-secondary pt-28">
        <div className="container-apple">
          <PageBreadcrumb 
            items={[{ name: 'Blog', path: '/blog' }]} 
            className="mb-6"
          />
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-semibold text-foreground mb-6 opacity-0 animate-fade-in">
              Apple Repair Tips & Guides
              <span className="block text-primary mt-2">For Bihar & Patna</span>
            </h1>
            <p className="text-lg text-muted-foreground opacity-0 animate-fade-in animation-delay-100">
              Expert tips, repair guides, and helpful information about Apple device maintenance and repair services in Bihar.
            </p>
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="section-padding">
        <div className="container-apple">
          <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-8 text-center">
            Latest Repair Guides & Tips
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group bg-card rounded-3xl border border-border overflow-hidden hover:border-primary/30 hover:shadow-apple-md transition-all opacity-0 animate-fade-in"
                style={{ animationDelay: `${index * 100}ms`, animationFillMode: 'forwards' }}
              >
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                      {post.category}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-semibold text-foreground mb-3 group-hover:text-primary transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  
                  <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(post.date).toLocaleDateString('en-IN', { 
                          day: 'numeric', 
                          month: 'short', 
                          year: 'numeric' 
                        })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {post.readTime}
                      </span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transform translate-x-0 group-hover:translate-x-1 transition-all" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Links Section for Internal Linking */}
      <section className="section-padding bg-muted/30">
        <div className="container-apple">
          <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-6 text-center">
            Popular Repair Services in Patna
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Link to="/iphone-service" className="bg-card p-6 rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-md transition-all text-center group">
              <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2">iPhone Repair</h3>
              <p className="text-sm text-muted-foreground">Screen, battery & all repairs</p>
            </Link>
            <Link to="/macbook-service" className="bg-card p-6 rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-md transition-all text-center group">
              <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2">MacBook Repair</h3>
              <p className="text-sm text-muted-foreground">Logic board & display fixes</p>
            </Link>
            <Link to="/ipad-service" className="bg-card p-6 rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-md transition-all text-center group">
              <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2">iPad Repair</h3>
              <p className="text-sm text-muted-foreground">Screen & charging port repair</p>
            </Link>
            <Link to="/watch-service" className="bg-card p-6 rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-md transition-all text-center group">
              <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2">Apple Watch Repair</h3>
              <p className="text-sm text-muted-foreground">Battery & screen replacement</p>
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <Link to="/iphone-screen-replacement" className="text-primary hover:underline">iPhone Screen Replacement</Link>
            <span className="text-border">•</span>
            <Link to="/iphone-battery-replacement" className="text-primary hover:underline">iPhone Battery Replacement</Link>
            <span className="text-border">•</span>
            <Link to="/service-areas" className="text-primary hover:underline">Service Areas in Bihar</Link>
            <span className="text-border">•</span>
            <Link to="/franchise" className="text-primary hover:underline">Franchise Opportunity</Link>
            <span className="text-border">•</span>
            <Link to="/updates" className="text-primary hover:underline">Latest Offers</Link>
          </div>
        </div>
      </section>

      {/* SEO Content */}
      <section className="section-padding bg-secondary">
        <div className="container-apple">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-4">
              Apple Device Repair Resources in Bihar
            </h2>
            <p className="text-muted-foreground mb-6">
              At iFix Apple Care, we believe in empowering our customers with knowledge. Our blog provides valuable insights 
              about Apple device maintenance, repair tips, and the latest updates about our services in Patna and across Bihar. 
              Whether you need help with your <Link to="/iphone-service" className="text-primary hover:underline">iPhone</Link>, 
              <Link to="/macbook-service" className="text-primary hover:underline"> MacBook</Link>, 
              <Link to="/ipad-service" className="text-primary hover:underline"> iPad</Link>, or 
              <Link to="/watch-service" className="text-primary hover:underline"> Apple Watch</Link>, 
              our guides will help you make informed decisions about your device repairs.
            </p>
            <p className="text-muted-foreground mb-8">
              Serving customers across Patna, Gaya, Muzaffarpur, Bhagalpur, Darbhanga, and all districts of Bihar with 
              professional Apple device repair services.
            </p>
            
            {/* Quick service links */}
            <div className="flex flex-wrap justify-center gap-4 pt-6 border-t border-border">
              <Link to="/iphone-service" className="text-primary hover:underline font-medium">
                iPhone Repair
              </Link>
              <span className="text-border">•</span>
              <Link to="/macbook-service" className="text-primary hover:underline font-medium">
                MacBook Repair
              </Link>
              <span className="text-border">•</span>
              <Link to="/ipad-service" className="text-primary hover:underline font-medium">
                iPad Repair
              </Link>
              <span className="text-border">•</span>
              <Link to="/watch-service" className="text-primary hover:underline font-medium">
                Watch Repair
              </Link>
              <span className="text-border">•</span>
              <Link to="/#contact" className="text-primary hover:underline font-medium">
                Contact Us
              </Link>
              <span className="text-border">•</span>
              <Link to="/franchise" className="text-primary hover:underline font-medium">
                Franchise
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Blog;